import { useEffect, useRef, useState } from 'react'
import type { ActionsBandeau, ActionsBandeauParams } from '@/platform/lib/solutionRegistry'
import { useProfile } from '@/platform/hooks/useProfile'
import { useRetourDuFocus } from '@/platform/hooks/useRetourDuFocus'
import { useConfirmationAudit } from '@/platform/components/shared/useConfirmationAudit'
import { Button } from '@/platform/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/platform/components/ui/dialog'
import { useStartAudit } from './useStartAudit'
import { AuditProgressSheet } from '../components/audit/AuditProgressSheet'

/**
 * Spec 161, second retour de Christophe sur `dev` (2026-10-12) — « Lancer l'audit » et « Suivre l'audit » quittent le
 * Tableau de bord pour le bandeau, en menu sur la date du dernier audit. La solution les déclare au registre
 * (`useActionsBandeau`) ; la plateforme les liste sans savoir ce qu'ils font.
 *
 * Mêmes règles qu'avant (specs 084, 160) : administrateur, consultant et revendeur seulement — jamais le client, ni la
 * Vue client (`enabled`, posé par la plateforme) ; confirmation qui nomme le client ; le serveur refuse de toute façon
 * un client hors périmètre. Ce que le bandeau ne peut pas montrer dans ses 56 px passe en portail : le volet de suivi,
 * qui s'ouvre sur « Audit lancé pour … », et une boîte qui dit pourquoi un lancement a échoué — sans elle, on recréait
 * le défaut qui a fait retirer « Demander un audit » (un échec qui ne s'affiche pas).
 *
 * Tous les hooks sont appelés à chaque rendu, `enabled` ou non (registre : nombre de hooks stable).
 */
export function useActionsAuditBandeau({ accountId, nomClient, enabled }: ActionsBandeauParams): ActionsBandeau {
  const { profile } = useProfile()
  const autorise = profile?.role === 'Admin' || profile?.role === 'Consultant' || profile?.role === 'Reseller'
  const actif = enabled && autorise && accountId !== null && nomClient.trim() !== ''
  const { startAudit, starting, error, clearError } = useStartAudit()
  // La confirmation vit avec le client affiché ET le droit d'agir : quitter l'un ou l'autre (autre client, Vue client,
  // page sans date) la ferme — « Confirmer » ne peut plus lancer l'audit d'un contexte quitté (revue à froid).
  const confirmation = useConfirmationAudit('lancement', actif ? accountId : null)
  const [suivi, setSuivi] = useState<{ accountId: string; nomClient: string; avis: string | null } | null>(null)
  const retourEchec = useRetourDuFocus()

  // Chaque changement de client ou de droit ouvre une nouvelle « génération ». Une réponse de lancement arrivée après
  // ce changement n'ouvre rien, et son échec ne s'affiche pas — même si l'on est revenu entre-temps au client de départ
  // (aller-retour A → B → A pendant la requête, revue à froid).
  const generationRef = useRef(0)
  const [generation, setGeneration] = useState(0)
  const [generationLancement, setGenerationLancement] = useState<number | null>(null)
  useEffect(() => {
    generationRef.current += 1
    setGeneration(generationRef.current)
    setSuivi(null)
    clearError()
  }, [accountId, actif, clearError])

  async function lancer(id: string, nom: string) {
    const gen = generationRef.current
    setGenerationLancement(gen)
    const res = await startAudit(id)
    if (gen !== generationRef.current) return
    if (res) setSuivi({ accountId: id, nomClient: nom, avis: `Audit lancé pour ${nom}.` })
  }

  const items = actif && accountId !== null ? [
    {
      id: 'lancer-audit',
      libelle: starting === accountId ? 'Lancement…' : 'Lancer l\'audit…',
      desactivee: starting === accountId,
      onSelect: () => confirmation.confirmer(nomClient, () => { void lancer(accountId, nomClient) }),
    },
    {
      id: 'suivre-audit',
      libelle: 'Suivre l\'audit',
      onSelect: () => setSuivi({ accountId, nomClient, avis: null }),
    },
  ] : []

  // L'échec ne vaut que pour le client affiché (`useStartAudit` le date par compte) et pour un lancement de la
  // génération courante ; rien ne s'affiche hors du droit d'agir.
  const echec = actif && error !== null && error.accountId === accountId && generationLancement === generation ? error : null
  const suiviCourant = actif && suivi !== null && suivi.accountId === accountId ? suivi : null

  const element = (
    <>
      {confirmation.element}
      <AuditProgressSheet
        open={suiviCourant !== null}
        onOpenChange={o => { if (!o) setSuivi(null) }}
        accountId={suiviCourant?.accountId ?? null}
        displayName={suiviCourant?.nomClient}
        avis={suiviCourant?.avis ?? null}
      />
      <Dialog open={echec !== null} onOpenChange={o => { if (!o) clearError() }}>
        <DialogContent role="alertdialog" onOpenAutoFocus={() => retourEchec.memoriser()} onCloseAutoFocus={retourEchec.rendre}>
          <DialogHeader>
            <DialogTitle>Audit non lancé</DialogTitle>
          </DialogHeader>
          <DialogDescription className="px-6 py-4">
            {echec?.message}
            {echec?.missing && echec.missing.length > 0 && <> ({echec.missing.join(', ')}) — complétez la fiche client.</>}
          </DialogDescription>
          <DialogFooter>
            <Button onClick={clearError}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )

  return { items, element }
}
